import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, BookOpen, CheckCircle2, Circle, ClipboardList, AlertCircle } from "lucide-react";
import { UniHeader } from "./_lmsShared";

export default function UniversityCourse() {
  const { enrollmentId, courseCode } = useParams();
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        universityApi.lmsModules(courseCode, enrollmentId),
        universityApi.assignmentsListByCourse(courseCode, enrollmentId).catch(() => ({ data: { items: [] } })),
      ]);
      setData(m.data);
      setAssignments(a.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load course"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [enrollmentId, courseCode]);

  const statusBadge = (sub, passing) => {
    if (!sub) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Not submitted</span>;
    if (sub.status === "graded") {
      const pct = sub.score || 0;
      const passed = pct >= (passing || 80);
      return (
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${passed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
          Graded · {pct}% {passed ? "PASSED" : "NOT PASSED"}
        </span>
      );
    }
    if (sub.status === "revision_requested") {
      return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Revision requested</span>;
    }
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Submitted · Awaiting grade</span>;
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Course" back="/university/student" />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="uni-course-page">
        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : !data ? (
          <div className="text-slate-500">Course unavailable.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
              <BookOpen className="w-3.5 h-3.5" />Course · <span className="font-mono">{data.course_code}</span>
            </div>
            <h1 className="font-display text-3xl text-slate-900">Course Modules</h1>
            <div className="mt-1 text-sm text-slate-600">
              {data.lessons_completed}/{data.lessons_total} lessons complete · {data.progress_percent}%
            </div>

            {/* Modules + lessons */}
            <div className="mt-6 space-y-5">
              {(data.modules || []).length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                  No modules have been published for this course yet. Please check back soon.
                </div>
              )}
              {(data.modules || []).map((m) => (
                <section key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`uni-module-${m.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display text-lg text-slate-900">{m.title}</div>
                      {m.description && <p className="text-sm text-slate-600 mt-0.5">{m.description}</p>}
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">
                      {m.lessons_completed}/{m.lessons_total} · {m.progress_percent}%
                    </div>
                  </div>
                  <ul className="mt-4 divide-y divide-slate-100">
                    {(m.lessons || []).map((l) => (
                      <li key={l.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`uni-lesson-${l.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {l.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                          <span className="text-sm text-slate-900 truncate">{l.title}</span>
                          {l.duration_minutes ? <span className="text-[11px] text-slate-400 ml-1">{l.duration_minutes}m</span> : null}
                        </div>
                        <Link
                          to={`/university/lessons/${l.id}?enrollment_id=${enrollmentId}`}
                          className="text-xs text-purple-900 hover:underline"
                          data-testid={`uni-lesson-open-${l.id}`}
                        >
                          {l.completed ? "Review" : "Open lesson"} →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/* Assignments section */}
            <section className="mt-8" data-testid="uni-course-assignments">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-3">
                <ClipboardList className="w-3.5 h-3.5" />Assignments
              </div>
              {assignments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                  No assignments published for this course.
                </div>
              ) : (
                <div className="space-y-3">
                  {assignments.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3"
                      data-testid={`uni-assignment-${a.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-display text-base text-slate-900 truncate">{a.title}</span>
                          {a.required && (
                            <span className="text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">Required</span>
                          )}
                        </div>
                        {a.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.description}</p>}
                        <div className="mt-1.5 flex items-center gap-2">
                          {statusBadge(a.my_submission, a.passing_score)}
                          <span className="text-[11px] text-slate-400">Passing ≥ {a.passing_score || 80}%</span>
                        </div>
                      </div>
                      <Link
                        to={`/university/assignments/${a.id}?enrollment_id=${enrollmentId}&course_code=${courseCode}`}
                        className="text-xs text-purple-900 hover:underline whitespace-nowrap"
                        data-testid={`uni-assignment-open-${a.id}`}
                      >
                        {a.my_submission ? "View / Resubmit" : "Open & submit"} →
                      </Link>
                    </div>
                  ))}
                  <div className="text-[11px] text-slate-500 flex items-start gap-1.5 pt-1">
                    <AlertCircle className="w-3 h-3 mt-0.5" />
                    Course completion requires all lessons, quizzes, and assignments to pass ≥ 80%.
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
